export type MiMode = "PMI" | "SMI";

export const MI_INSTANCE_COUNT_KEY = "mi.instanceCount";
export const MI_PMI_QUORUM_KEY = "mi.pmi.quorum";
export const MI_SMI_CONTINUE_KEY = "mi.smi.continue";
export const MI_SMI_CANCEL_KEY = "mi.smi.cancel";
export const MI_MODE_KEY = "mi.mode";
export const MI_COMPLETED_COUNT_KEY = "mi.completedCount";
export const MI_SUCCEEDED_COUNT_KEY = "mi.succeededCount";
export const MI_FAILED_COUNT_KEY = "mi.failedCount";
export const MI_AGG_CHILD_KEY = "mi.aggregate.childOutputKey";
export const MI_AGG_STRATEGY_KEY = "mi.aggregate.strategy";
export const MI_AGG_TARGET_KEY = "mi.aggregate.aggregateTarget";
export const MI_AGG_COLLECT_KEY = "mi.aggregate.collectAllTarget";
export const MI_COLLECTION_SOURCE_KEY = "mi.collectionSource";
export const MI_EXPANSION_MODE_KEY = "mi.expansionMode";
export const MI_ORDERING_KEY = "mi.ordering";

export type MiAggregateStrategy = "Majority" | "SumInt" | "SumDouble" | "AverageDouble" | "Min" | "Max";

export type MiInstanceCount = { kind: "fixed"; value: number } | { kind: "symbol"; value?: any };

// Check multi instance is enabled
export function getModelerSafe(): any | undefined {
  const w: any = (typeof window !== "undefined" ? window : {}) as any;
  const cands = [w.bpmnModeler, w.modeler, w.bpmnjs, w.viewer].filter(Boolean);
  for (const m of cands) {
    try {
      const er = m?.get?.("elementRegistry");
      if (er?.get) return m;
    } catch {}
  }
  return undefined;
}

export function getBpmnMiState(stepId?: string): { enabled: boolean; mode?: MiMode; isSequential?: boolean } {
  if (!stepId) return { enabled: false };
  const mdl = getModelerSafe();
  if (!mdl) return { enabled: false };

  try {
    const er = mdl.get("elementRegistry");
    const el = er.get(stepId);
    const lc = el?.businessObject?.loopCharacteristics;
    const enabled = !!lc && lc.$type === "bpmn:MultiInstanceLoopCharacteristics";
    const isSequential: boolean | undefined = enabled ? !!lc.isSequential : undefined;
    const mode: MiMode | undefined = enabled ? (lc.isSequential ? "SMI" : "PMI") : undefined;
    return { enabled, mode, isSequential };
  } catch {
    return { enabled: false };
  }
}

function ensureExtraArray(step: any): any[] {
  if (!step.extraParameters) step.extraParameters = [] as any[];
  return step.extraParameters as any[];
}

function readJsonFromExtra(step: any, key: string): any | undefined {
  const arr = ensureExtraArray(step);
  const hit = (arr as any[]).find((p) => p?.key === key);
  if (!hit?.value) return undefined;
  try {
    return JSON.parse(hit.value);
  } catch {
    return undefined;
  }
}

async function writeJsonToExtra(sd: any, step: any, key: string, obj: any, reason?: any): Promise<void> {
  const arr = ensureExtraArray(step);
  const str = JSON.stringify(obj);
  const hit = (arr as any[]).find((p) => p?.key === key);
  if (hit) hit.value = str;
  else (arr as any[]).push({ key, value: str } as any);
  try {
    console.log("[MI][WRITE]", { stepId: step?.id, key, raw: str });
  } catch {}
  if (sd?.saveWorkFlow && sd?.selectedWf) {
    await sd.saveWorkFlow(sd.selectedWf, reason);
  }
}

export function readMiInstanceCount(step: any) {
  return readJsonFromExtra(step, MI_INSTANCE_COUNT_KEY);
}

export async function writeMiInstanceCount(sd: any, step: any, payload: MiInstanceCount, reason?: any) {
  await writeJsonToExtra(sd, step, MI_INSTANCE_COUNT_KEY, payload, reason);
}

export function readMiCondition(step: any, key: string) {
  return readJsonFromExtra(step, key);
}

export async function writeMiCondition(sd: any, step: any, key: string, cond: any, reason?: any) {
  await writeJsonToExtra(sd, step, key, cond, reason);
}

// Always the mode name. The stored mode is only a mirror of the diagram marker, but it is the one
// readers fall back to when there is no diagram, so it keeps a single spelling.
export async function writeMiMode(sd: any, step: any, mode: MiMode, reason?: any): Promise<void> {
  await writeJsonToExtra(sd, step, MI_MODE_KEY, mode, reason);
}

export interface IMiModeExtraParameter {
  key?: string;
  value?: string;
}

export interface IMiModeStep {
  id?: string;
  extraParameters?: IMiModeExtraParameter[];
}

// A stored mi.mode, in either spelling a saved process can carry: the mode name ("\"PMI\"" /
// "\"SMI\"") or, from before the panels mirrored the name, the marker's isSequential (false / true).
// Undefined for anything else, including "None".
export function parseStoredMiMode(raw: string | undefined): MiMode | undefined {
  if (!raw) return undefined;
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return undefined;
  }
  if (value === "PMI" || value === "SMI") return value;
  if (value === true) return "SMI";
  if (value === false) return "PMI";
  return undefined;
}

// The mode a step runs in, decided the way the code generator decides it (StepRepetition on the
// backend): the diagram marker whenever the modeler knows the element — no marker means the step is
// not multi-instance, whatever a stale mi.mode says — and the stored mi.mode only when there is no
// diagram to ask.
export function resolveMiMode(step: IMiModeStep | undefined): MiMode | undefined {
  const stepId: string | undefined = step?.id;
  const element: unknown = stepId ? getModelerSafe()?.get?.("elementRegistry")?.get?.(stepId) : undefined;
  if (element) return getBpmnMiState(stepId).mode;

  const stored: IMiModeExtraParameter | undefined = (step?.extraParameters ?? []).find(
    (p: IMiModeExtraParameter): boolean => p?.key === MI_MODE_KEY
  );
  return parseStoredMiMode(stored?.value);
}

function readStringFromExtra(step: any, key: string): string | undefined {
  const raw = readJsonFromExtra(step, key);
  if (raw == null) return undefined;
  return typeof raw === "string" ? raw : undefined;
}

export function readMiAggregation(step: any): {
  childOutputKey?: string;
  strategy?: MiAggregateStrategy;
  aggregateTarget?: string;
  collectAllTarget?: string;
} {
  return {
    childOutputKey: readStringFromExtra(step, MI_AGG_CHILD_KEY),
    strategy: readStringFromExtra(step, MI_AGG_STRATEGY_KEY) as MiAggregateStrategy | undefined,
    aggregateTarget: readStringFromExtra(step, MI_AGG_TARGET_KEY),
    collectAllTarget: readStringFromExtra(step, MI_AGG_COLLECT_KEY)
  };
}

export async function writeMiAggregationChildKey(sd: any, step: any, key: string, reason?: any) {
  await writeJsonToExtra(sd, step, MI_AGG_CHILD_KEY, key, reason);
}

export async function writeMiAggregationStrategy(sd: any, step: any, s: MiAggregateStrategy, reason?: any) {
  await writeJsonToExtra(sd, step, MI_AGG_STRATEGY_KEY, s, reason);
}

export async function writeMiAggregationAggregateTarget(sd: any, step: any, t: string, reason?: any) {
  await writeJsonToExtra(sd, step, MI_AGG_TARGET_KEY, t, reason);
}

export async function writeMiAggregationCollectAllTarget(sd: any, step: any, t: string | undefined, reason?: any) {
  if (!t) return;
  await writeJsonToExtra(sd, step, MI_AGG_COLLECT_KEY, t, reason);
}

export function readMiCollectionSource(step: any): string {
  return readStringFromExtra(step, MI_COLLECTION_SOURCE_KEY) ?? "Manual";
}

export async function writeMiCollectionSource(
  sd: any,
  step: any,
  source: "Manual" | "Assignment",
  reason?: any
): Promise<void> {
  return writeJsonToExtra(sd, step, MI_COLLECTION_SOURCE_KEY, source, reason);
}

export function readMiExpansionMode(step: any): string {
  return readStringFromExtra(step, MI_EXPANSION_MODE_KEY) ?? "ByRole";
}

export async function writeMiExpansionMode(sd: any, step: any, mode: "ByRole" | "ByUser", reason?: any): Promise<void> {
  return writeJsonToExtra(sd, step, MI_EXPANSION_MODE_KEY, mode, reason);
}

export function readMiOrdering(step: any): string {
  return readStringFromExtra(step, MI_ORDERING_KEY) ?? "Ascending";
}

export async function writeMiOrdering(
  sd: any,
  step: any,
  mode: "Ascending" | "Descending",
  reason?: any
): Promise<void> {
  return writeJsonToExtra(sd, step, MI_ORDERING_KEY, mode, reason);
}

export function watchBpmnMi(stepId: string, onChange: () => void, pollMs = 900): () => void {
  if (!stepId) return () => {};

  let disposed = false;
  let last = JSON.stringify(getBpmnMiState(stepId));

  const tick = () => {
    if (disposed) return;
    const now = JSON.stringify(getBpmnMiState(stepId));
    if (now !== last) {
      last = now;
      try {
        onChange();
      } catch {}
    }
  };

  const mdl = getModelerSafe();
  let offFns: Array<() => void> = [];
  let timer: any;

  try {
    const eb = mdl?.get?.("eventBus");
    if (eb?.on) {
      const h = () => tick();
      eb.on("commandStack.changed", h);
      eb.on("elements.changed", h);
      eb.on("selection.changed", h);
      offFns.push(() => {
        try {
          eb.off && eb.off("commandStack.changed", h);
        } catch {}
      });
      offFns.push(() => {
        try {
          eb.off && eb.off("elements.changed", h);
        } catch {}
      });
      offFns.push(() => {
        try {
          eb.off && eb.off("selection.changed", h);
        } catch {}
      });

      timer = setInterval(tick, pollMs);
    } else {
      timer = setInterval(tick, pollMs);
    }
  } catch {
    timer = setInterval(tick, pollMs);
  }

  tick();

  return () => {
    disposed = true;
    offFns.forEach((f) => f && f());
    if (timer) clearInterval(timer);
  };
}
